import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface QuoteItem {
  id: string;
  name: string;
  category: string;
  price?: number;         // price_private
  price_public?: number;  // public event price
  tags?: string[];
  // Note: id is already included and will be sent to the edge function
  // for load sheet item aggregation
}

interface QuoteContextType {
  items: QuoteItem[];
  addItem: (item: QuoteItem) => void;
  removeItem: (id: string) => void;
  replaceItem: (oldId: string, newItem: QuoteItem) => void;
  clearItems: () => void;
  isInQuote: (id: string) => boolean;
  itemCount: number;
  subtotal: number;
  eventDate: string | null;
  setEventDate: (date: string | null) => void;
  eventStartTime: string | null;
  setEventStartTime: (time: string | null) => void;
  setCartEmail: (email: string) => void;
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

const STORAGE_KEY = "redline-quote-items";
const DATE_STORAGE_KEY = "redline-event-date";
const START_TIME_STORAGE_KEY = "redline-event-start-time";
const MAX_ITEMS = 10;
const SESSION_KEY = "rl_session_id";
const SYNC_DEBOUNCE_MS = 2000;

function getSessionId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function QuoteProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<QuoteItem[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [eventDate, setEventDateState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(DATE_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const eventDateRef = useRef<string | null>(eventDate);

  const [eventStartTime, setEventStartTimeState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(START_TIME_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const eventStartTimeRef = useRef<string | null>(eventStartTime);
  const emailRef = useRef<string | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fire-and-forget sync to server
  const syncDraftCart = useCallback((cartItems: QuoteItem[]) => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const sessionId = getSessionId();
      if (!sessionId) return;
      supabase.functions.invoke('sync-draft-cart', {
        body: {
          session_id: sessionId,
          items: cartItems.map(i => ({
            id: i.id,
            name: i.name,
            category: i.category,
            price: i.price,
            price_public: i.price_public,
            tags: i.tags,
          })),
          event_date: eventDateRef.current,
          event_start_time: eventStartTimeRef.current,
          email: emailRef.current,
        },
      }).catch(() => { /* silent */ });
    }, SYNC_DEBOUNCE_MS);
  }, []);

  // Persist to localStorage + trigger sync
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    syncDraftCart(items);
  }, [items, syncDraftCart]);

  const setEventDate = useCallback((date: string | null) => {
    eventDateRef.current = date;
    setEventDateState(date);
    try {
      if (date) localStorage.setItem(DATE_STORAGE_KEY, date);
      else localStorage.removeItem(DATE_STORAGE_KEY);
    } catch { /* silent */ }
    syncDraftCart(items);
  }, [items, syncDraftCart]);

  const setEventStartTime = useCallback((time: string | null) => {
    eventStartTimeRef.current = time;
    setEventStartTimeState(time);
    try {
      if (time) localStorage.setItem(START_TIME_STORAGE_KEY, time);
      else localStorage.removeItem(START_TIME_STORAGE_KEY);
    } catch { /* silent */ }
    syncDraftCart(items);
  }, [items, syncDraftCart]);

  const setCartEmail = useCallback((email: string) => {
    if (!email || !email.includes('@')) return;
    emailRef.current = email.trim().toLowerCase();
    syncDraftCart(items);
  }, [items, syncDraftCart]);

  const addItem = (item: QuoteItem) => {
    if (items.some((i) => i.id === item.id)) {
      toast.info(`${item.name} is already in your event`);
      return;
    }

    if (items.length >= MAX_ITEMS) {
      toast.error(`Maximum ${MAX_ITEMS} items allowed. Please remove some items first.`);
      return;
    }

    setItems((prev) => [...prev, item]);
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const replaceItem = (oldId: string, newItem: QuoteItem) => {
    const oldItem = items.find((i) => i.id === oldId);
    if (!oldItem) return;
    
    setItems((prev) => prev.map((item) => 
      item.id === oldId ? newItem : item
    ));
    toast.success(`Swapped ${oldItem.name} for ${newItem.name}`);
  };

  const clearItems = () => {
    setItems([]);
  };

  const isInQuote = (id: string) => items.some((i) => i.id === id);

  const subtotal = items.reduce((sum, item) => sum + (item.price || 0), 0);

  return (
    <QuoteContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        replaceItem,
        clearItems,
        isInQuote,
        itemCount: items.length,
        subtotal,
        eventDate,
        setEventDate,
        eventStartTime,
        setEventStartTime,
        setCartEmail,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
}

export function useQuote() {
  const context = useContext(QuoteContext);
  if (context === undefined) {
    throw new Error("useQuote must be used within a QuoteProvider");
  }
  return context;
}
